import { useFetch } from "@/hooks/useFetch";
import { PageHeader, Panel, Loading, Tag } from "@/components/common";
import { BookBookmark, Palette, MusicNotes, Translate, ShieldCheck, Camera, Clock } from "@phosphor-icons/react";

const SONIC = ["Sonic Logo", "Main Theme", "Happy Theme", "Adventure Theme", "Gentle/Sad Theme", "Mystery Theme", "Victory Theme", "Bedtime Theme", "Outro Theme"];

export default function StudioBible() {
  const { data: s, loading } = useFetch("/studios/current");
  if (loading || !s) return <Loading />;

  return (
    <div>
      <PageHeader icon={BookBookmark} title="Studio Bible"
        subtitle="The permanent identity and rules of this studio. AI generation automatically loads this before creating any content."
        badge={<span className="h-9 w-9 rounded-lg flex items-center justify-center text-xl" style={{ background: `${s.color}22`, border: `1px solid ${s.color}55` }}>{s.emoji}</span>} />

      <Panel className="mb-6 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full opacity-20 blur-3xl" style={{ background: s.color }} />
        <h2 className="font-display text-3xl font-black text-white">{s.name}</h2>
        <p className="text-zinc-300 mt-2 max-w-3xl">{s.concept}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {s.genre && <Tag>{s.genre}</Tag>}{s.audience && <Tag>{s.audience}</Tag>}
          {s.age_range && <Tag>Age {s.age_range}</Tag>}{s.style && <Tag>{s.style}</Tag>}
        </div>
      </Panel>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel>
          <Head icon={ShieldCheck} color="#00FF9D">Storytelling & Content Rules</Head>
          <Field label="Tone" value={s.tone} />
          <Field label="Comedy level" value={s.comedy_level} />
          <Field label="Intensity / restrictions" value={s.intensity_rules || s.content_restrictions} />
          <Field label="Story direction" value={s.story_direction} />
        </Panel>

        <Panel>
          <Head icon={Palette} color="#00E5FF">Visual Identity</Head>
          <Field label="Animation style" value={s.style} />
          <Field label="Visual rules" value={s.visual_identity} />
          <Field label="Camera language" value={s.camera_language} />
          <div className="mt-2 flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg border border-white/10" style={{ background: s.color }} />
            <span className="font-mono text-xs text-zinc-500">{s.color}</span>
          </div>
        </Panel>

        <Panel>
          <Head icon={MusicNotes} color="#FF007F">Sonic Identity</Head>
          <p className="text-sm text-zinc-400 mb-3">{s.music_direction}</p>
          <div className="flex flex-wrap gap-2">{SONIC.map((t) => <Tag key={t}>{t}</Tag>)}</div>
          <p className="mt-3 text-xs text-zinc-500">Each theme can be locked as official studio music with licensing/provenance.</p>
        </Panel>

        <Panel>
          <Head icon={Translate} color="#8B5CF6">Language & Publishing</Head>
          <Field label="Main language" value={s.main_language} />
          <div className="mb-3"><div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Languages</div><div className="flex flex-wrap gap-2">{(s.languages || []).map((l) => <Tag key={l}>{l}</Tag>)}</div></div>
          <Field label="YouTube strategy" value={s.youtube_strategy} />
        </Panel>
      </div>

      {s.default_episode_minutes && (
        <Panel className="mt-6 flex flex-wrap items-center gap-4">
          <Clock size={22} weight="duotone" className="text-[#FFB800]" />
          <span className="text-sm text-white">Default full episode: <span className="font-bold">{s.default_episode_minutes} min</span></span>
          {s.episode_durations && Object.entries(s.episode_durations).map(([k, v]) => <Tag key={k}>{k}: {v}</Tag>)}
        </Panel>
      )}
    </div>
  );
}

function Head({ icon: Icon, color, children }) {
  return <div className="flex items-center gap-2 mb-4"><Icon size={18} weight="duotone" style={{ color }} /><span className="font-display font-bold text-white">{children}</span></div>;
}
function Field({ label, value }) {
  if (!value) return null;
  return <div className="mb-3"><div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div><div className="text-sm text-white mt-0.5">{value}</div></div>;
}
